/**
 * <SupportBoard> — the support team's view of every issue.
 *
 * Two views over one query: a KANBAN whose columns are the lifecycle's board
 * columns (`meta.lifecycle.columns`, each state placed by its own `column`) and
 * a sortable LIST for scanning. The columns are data — a lifecycle that gains
 * a stage gains a column here with no code change.
 *
 * Opening a card shows the shared <IssueDetail> in a side panel, so the board
 * stays in view behind it; a change made there updates the card in place.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { AlertTriangle, Columns3, List, Plus, RefreshCw, Search, User } from 'lucide-react'

import type {
  IssueDetail as IssueDetailData, IssueSummary, SupportMeta, SupportNotify, SupportRuntime,
} from './contract'
import IssueDetail from './IssueDetail'
import ReportIssueDialog from './ReportIssueDialog'
import {
  Empty, ErrorLine, Pill, Spinner, SupportDrawer, SupportScope, btn, errorText, inputStyle,
  mutedText, severityTone, stateLabel, statusTone, timeAgo, vocabLabel,
} from './ui'

export interface SupportBoardProps {
  runtime: SupportRuntime
  meta: SupportMeta
  onOpenStaging?: (issueNumber: number) => void
  notify?: SupportNotify
  /** Open this issue on mount (a deep link / notification). */
  initialIssue?: number | null
  /** Reports which issue is open, so the host can reflect it in its URL. */
  onIssueChange?: (issueNumber: number | null) => void
  /** Offer "New issue" (the builder files issues too); its `source`. */
  newIssueSource?: string
  getContext?: () => Record<string, unknown>
}

type View = 'kanban' | 'list'

const VIEW_KEY = 'twasta_support_board_view'

function readView(): View {
  try { return (localStorage.getItem(VIEW_KEY) as View) === 'list' ? 'list' : 'kanban' } catch { return 'kanban' }
}

export default function SupportBoard({
  runtime, meta, onOpenStaging, notify, initialIssue, onIssueChange, newIssueSource, getContext,
}: SupportBoardProps) {
  const [view, setView] = useState<View>(readView)
  const [type, setType] = useState('')
  const [severity, setSeverity] = useState('')
  const [tenant, setTenant] = useState('')
  const [q, setQ] = useState('')
  const [qApplied, setQApplied] = useState('')
  const [openOnly, setOpenOnly] = useState(false)
  const [mineOnly, setMineOnly] = useState(false)
  const [rows, setRows] = useState<IssueSummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [current, setCurrent] = useState<number | null>(initialIssue || null)
  const [reporting, setReporting] = useState(false)
  // Organizations seen so far, so the filter keeps offering them while it is
  // itself narrowing the list.
  const tenantsSeen = useRef<Set<string>>(new Set())

  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view) } catch { /* private mode */ } }, [view])
  useEffect(() => { if (initialIssue) setCurrent(initialIssue) }, [initialIssue])

  // Search waits for the typing to pause.
  useEffect(() => {
    const id = window.setTimeout(() => setQApplied(q.trim()), 350)
    return () => window.clearTimeout(id)
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await runtime.listIssues({
        scope: 'all', type: type || undefined, severity: severity || undefined,
        tenant: tenant || undefined, q: qApplied || undefined, open: openOnly,
        assignee: mineOnly ? 'me' : undefined, limit: 500,
      })
      const list = res.issues || []
      list.forEach((i) => { if (i.tenant_id) tenantsSeen.current.add(i.tenant_id) })
      setRows(list)
      setTotal(res.total ?? list.length)
      setError('')
    } catch (e) {
      setError(errorText(e, 'The board could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [runtime, type, severity, tenant, qApplied, openOnly, mineOnly])

  useEffect(() => { void load() }, [load])

  const openIssue = (n: number | null) => {
    setCurrent(n)
    onIssueChange?.(n)
  }

  // A change made inside the detail updates its card without a reload.
  const onChanged = useCallback((d: IssueDetailData) => {
    setRows((cur) => (cur ? cur.map((r) => (r.number === d.number ? { ...r, ...summaryOf(d) } : r)) : cur))
  }, [])

  const lifecycle = meta.lifecycle
  const columnOf = useMemo(() => {
    const m = new Map<string, string>()
    lifecycle.states.forEach((s) => m.set(s.id, s.column))
    return m
  }, [lifecycle])

  const byColumn = useMemo(() => {
    const m = new Map<string, IssueSummary[]>()
    lifecycle.columns.forEach((c) => m.set(c.id, []))
    for (const r of rows || []) {
      const col = columnOf.get(r.status) || lifecycle.columns[0]?.id
      if (col) m.get(col)?.push(r)
    }
    return m
  }, [rows, columnOf, lifecycle])

  const small = { ...inputStyle, width: 'auto', padding: '0.3rem 0.45rem', fontSize: '0.8425rem' }

  return (
    <SupportScope style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {/* ── toolbar ── */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--sp-border)', borderRadius: 8, overflow: 'hidden' }}>
          <button type="button" onClick={() => setView('kanban')} title="Board"
            style={{ ...btn(view === 'kanban' ? 'subtle' : 'ghost', true), border: 'none', borderRadius: 0 }}>
            <Columns3 size={13} /> Board
          </button>
          <button type="button" onClick={() => setView('list')} title="List"
            style={{ ...btn(view === 'list' ? 'subtle' : 'ghost', true), border: 'none', borderRadius: 0 }}>
            <List size={13} /> List
          </button>
        </div>
        <div style={{ position: 'relative', minWidth: 180, flex: '1 1 180px', maxWidth: 280 }}>
          <Search size={12} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--sp-muted)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, text or #number"
            style={{ ...inputStyle, paddingLeft: 26, fontSize: '0.8425rem', padding: '0.3rem 0.45rem 0.3rem 26px' }} />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} style={small} aria-label="Type">
          <option value="">All types</option>
          {meta.policy.issue_types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={small} aria-label="Severity">
          <option value="">All severities</option>
          {meta.policy.severities.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {meta.me.sees_all_orgs && (
          <select value={tenant} onChange={(e) => setTenant(e.target.value)} style={small} aria-label="Organization">
            <option value="">All organizations</option>
            {Array.from(tenantsSeen.current).sort().map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <label style={{ ...mutedText, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> Open only
        </label>
        <label style={{ ...mutedText, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> Assigned to me
        </label>
        <span style={{ flex: 1 }} />
        <span style={mutedText}>{rows ? `${total} issue${total === 1 ? '' : 's'}` : ''}</span>
        <button type="button" style={btn('ghost', true)} onClick={() => { void load() }} title="Refresh">
          {loading ? <Spinner size={12} /> : <RefreshCw size={12} />}
        </button>
        {newIssueSource && (
          <button type="button" style={btn('primary', true)} onClick={() => setReporting(true)}>
            <Plus size={12} /> New issue
          </button>
        )}
      </div>

      <ErrorLine message={error} onRetry={() => { void load() }} />
      {rows === null && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading the board…</div>}

      {rows !== null && view === 'kanban' && (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', flex: 1, minHeight: 0, paddingBottom: 6 }}>
          {lifecycle.columns.map((c) => {
            const items = byColumn.get(c.id) || []
            return (
              <div key={c.id} style={{
                flex: '0 0 250px', display: 'flex', flexDirection: 'column', minHeight: 0,
                background: 'var(--sp-bg2)', border: '1px solid var(--sp-border)', borderRadius: 10,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 0.65rem',
                  borderBottom: '1px solid var(--sp-border)', fontWeight: 700, fontSize: '0.8625rem',
                }}>
                  {c.label}
                  <span style={{ ...mutedText, fontWeight: 600 }}>{items.length}</span>
                </div>
                <div style={{ overflowY: 'auto', padding: '0.45rem', flex: 1 }}>
                  {items.length === 0 && <div style={{ ...mutedText, padding: '0.4rem' }}>—</div>}
                  {items.map((i) => (
                    <IssueCard key={i.id} issue={i} meta={meta} onOpen={() => openIssue(i.number)}
                      active={current === i.number} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {rows !== null && view === 'list' && (
        rows.length === 0 ? <Empty>No issues match.</Empty> : (
          <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8625rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--sp-muted)' }}>
                  {['#', 'Title', 'Status', 'Type', 'Severity', 'Organization', 'Reporter', 'Updated'].map((h) => (
                    <th key={h} style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--sp-border)', fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.id} onClick={() => openIssue(i.number)} style={{
                    cursor: 'pointer', background: current === i.number ? 'var(--sp-surface)' : 'transparent',
                  }}>
                    <td style={td}>{i.number}</td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {i.title}
                      {i.sla_overdue && <> <Pill tone="error"><AlertTriangle size={10} /> overdue</Pill></>}
                    </td>
                    <td style={td}><Pill tone={statusTone(lifecycle, i.status)}>{stateLabel(lifecycle, i.status)}</Pill></td>
                    <td style={td}>{vocabLabel(meta, 'issue_types', i.type)}</td>
                    <td style={td}><Pill tone={severityTone(meta, i.severity)}>{vocabLabel(meta, 'severities', i.severity)}</Pill></td>
                    <td style={td}>{i.tenant_id || '—'}</td>
                    <td style={td}>{i.reporter_name || '—'}</td>
                    <td style={{ ...td, ...mutedText }}>{timeAgo(i.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <SupportDrawer open={current !== null} onClose={() => openIssue(null)}
        title={current ? `Issue #${current}` : ''} width={760}>
        {current !== null && (
          <IssueDetail runtime={runtime} number={current} meta={meta} notify={notify}
            onOpenStaging={onOpenStaging} onChanged={onChanged} />
        )}
      </SupportDrawer>

      {newIssueSource && (
        <ReportIssueDialog open={reporting} onClose={() => setReporting(false)} runtime={runtime}
          meta={meta} source={newIssueSource} notify={notify} getContext={getContext}
          onCreated={(i) => { void load(); openIssue(i.number) }} />
      )}
    </SupportScope>
  )
}

const td: CSSProperties = { padding: '0.45rem 0.5rem', borderBottom: '1px solid var(--sp-border)', verticalAlign: 'top' }

function summaryOf(d: IssueDetailData): Partial<IssueSummary> {
  return {
    title: d.title, status: d.status, type: d.type, severity: d.severity, priority: d.priority,
    labels: d.labels, risk: d.risk, assignee_id: d.assignee_id, updated_at: d.updated_at,
    sla_overdue: d.sla_overdue,
  }
}

function IssueCard({ issue: i, meta, onOpen, active }: {
  issue: IssueSummary
  meta: SupportMeta
  onOpen: () => void
  active: boolean
}) {
  return (
    <button type="button" onClick={onOpen} style={{
      display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', marginBottom: 6,
      padding: '0.5rem 0.55rem', borderRadius: 8, color: 'var(--sp-text)',
      background: active ? 'var(--sp-surface)' : 'var(--sp-bg)',
      border: `1px solid ${i.sla_overdue ? 'var(--sp-error)' : active ? 'var(--sp-accent)' : 'var(--sp-border)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        <span style={{ ...mutedText, fontWeight: 700 }}>#{i.number}</span>
        <Pill tone={severityTone(meta, i.severity)}>{vocabLabel(meta, 'severities', i.severity)}</Pill>
        {i.sla_overdue && <Pill tone="error" title={`SLA was due ${i.sla_due_at || ''}`}><AlertTriangle size={10} /> SLA</Pill>}
      </div>
      <div style={{ fontWeight: 600, fontSize: '0.8625rem', margin: '4px 0' }}>{i.title}</div>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill tone={statusTone(meta.lifecycle, i.status)}>{stateLabel(meta.lifecycle, i.status)}</Pill>
        <span style={mutedText}>{vocabLabel(meta, 'issue_types', i.type)}</span>
        {i.risk && <span style={mutedText}>· {i.risk} risk</span>}
      </div>
      <div style={{ ...mutedText, marginTop: 4, display: 'flex', gap: 4, alignItems: 'center' }}>
        <User size={10} /> {i.reporter_name || '—'}{i.tenant_id && meta.me.sees_all_orgs ? ` · ${i.tenant_id}` : ''}
        <span style={{ flex: 1 }} />{timeAgo(i.updated_at)}
      </div>
    </button>
  )
}
